/* eslint-disable react/style-prop-object */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useModal } from 'hooks/useModal';
import { useSelector } from 'react-redux';
import { bytesToSize } from 'utils/helpers';
import { upload } from 'utils/api';
import { AnimatePresence, motion } from 'framer-motion';
import { useCreateProjectMutation, useGetChestQuery, useFetchProjectMutation } from 'store/api';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { MicrophoneIcon, MusicalNoteIcon, MegaphoneIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';

import Modal from 'components/Modal';
import Input from 'components/Input';
import Select from 'components/Select';
import Button from 'components/Button';
import TrackCoverSelector from 'components/TrackCoverSelector';
import TrackCoverPreview from 'components/TrackCoverPreview';
import Dropdown from 'components/Dropdown';
import AutoComplete from 'components/AutoComplete';
import { getErrorCode } from 'utils/getErrorCode';
import { errorCodes } from 'utils/errorCodes';
import ProgressLine from 'components/UploadProgressBar';

const { STORAGE_LIMIT_EXCEEDED } = errorCodes;

export default function Upload() {
  const { roles } = require('data/config.json');
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { data } = useSelector((state) => state.auth.user);
  const { file } = useSelector((state) => state.upload);
  const navigate = useNavigate();

  const { onOpen: openFullStorageModal } = useModal('FullStorageModal');

  const { data: chest = {}, isLoading } = useGetChestQuery();
  const [createProject, { isLoading: isLoadingProject }] = useCreateProjectMutation();
  const [fetchProject, { isLoading: isLoadingProjectGet }] = useFetchProjectMutation();
  const { onOpen: openShareLinkModal } = useModal('ShareLinkModal');

  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState('');
  const [participants, setParticipants] = useState([]);
  const [progress, setProgress] = useState({
    loaded: 0,
    total: 0,
    error: '',
  });
  const [cover, setCover] = useState('');
  const [covers, setCovers] = useState([]);
  const [defaultCover, setDefaultCover] = useState('');
  const [loadingImage, setLoadingImage] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState();
  const [track, setTrack] = useState({
    name: '',
    version: '',
    album: '',
    userRole: 'artist',
    cover: '',
    fileId: '',
  });

  // Extract filename without extension for default name
  const getDefaultTrackName = () => {
    if (file && file.filename) {
      return file.filename.replace(/\.[^/.]+$/, ''); // Remove file extension
      // setTrack({ ...track, name: file.filename.replace(/\.[^/.]+$/, '') });
    }
    return '';
  };

  const {
    watch,
    register,
    formState: { errors },
  } = useForm({ mode: 'all', defaultValues: { name: getDefaultTrackName(), version: '1' } });

  track.name = watch('name');
  track.version = watch('version');

  useEffect(() => {
    if (roles) setFilteredRoles(roles.filter((role) => role !== 'listener'));
  }, [roles]);

  useEffect(() => {
    handleUpload();
  }, []);

  useEffect(() => {
    if (chest && chest.covers) {
      setDefaultCover(chest.covers.find((cover) => cover.default));

      let filtered = chest.covers.filter((cover) => !cover.default);

      filtered.forEach((cover) => {
        filtered.push(cover);
      });

      setCovers(filtered);
    }
  }, [chest]);

  const handleOnChangeRole = (event) => {
    setTrack({ ...track, userRole: event.target.value });
  };

  const handleUpload = async () => {
    const formData = new FormData();
    const blob = await fetch(file.blob)
      .then((r) => r.blob())
      .catch((e) => navigate('/my-chest'));
    const getFile = new File([blob], file.filename);

    formData.append('files', getFile, file.filename);

    try {
      const response = await upload.post('audio', formData, {
        headers: { Authorization: `Bearer ${user?.token}` },
        onUploadProgress: (progressEvent) => {
          setProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
          });
        },
      });

      if (response && response.data) {
        setTrack({ ...track, fileId: response.data.id });
      } else {
        throw new Error('No se recibió respuesta válida del servidor');
      }
    } catch (error) {
      if (getErrorCode(error) === STORAGE_LIMIT_EXCEEDED) {
        openFullStorageModal();
        setProgress({
          loaded: 0,
          error: t('upload.full_storage'),
        });
      } else {
        setProgress({
          loaded: 0,
          error: 'Error al subir el archivo',
        });
      }
    }
  };

  const handleUploadImage = async () => {
    const formData = new FormData();
    const blob = await fetch(preview.url).then((r) => r.blob());
    const getFile = new File([blob], preview.filename);

    formData.append('files', getFile, preview.filename);

    setLoadingImage(true);

    try {
      const response = await upload.post('image', formData, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });

      setCover(response.data.url);
      setTrack({
        ...track,
        cover: response.data.id,
      });
    } catch (error) {
      console.log(error);
    }

    setLoadingImage(false);
    setOpen(false);
  };

  const handleCreateProject = async () => {
    // Ensure the current user is always the owner
    const currentUser = {
      id: data.user_id,
      user: data.full_name,
      role: track.userRole,
      isOwner: true,
    };

    addOrUpdateParticipant(currentUser);

    let formatParticipants = { invitations: [], participants: [] };

    // Ensure the current user is always included as the owner
    formatParticipants.participants.push({
      role: track.userRole,
      user: data.user_id,
    });

    participants.forEach((participant) => {
      // Skip the current user as they're already added above
      if (participant.id === data.user_id) {
        return;
      }

      if (participant.isEmail) {
        formatParticipants.invitations.push({
          role: participant.role,
          email: participant.id,
        });
      } else {
        formatParticipants.participants.push({
          role: participant.role,
          user: participant.id,
        });
      }
    });

    const body = {
      name: track.name,
      cover: track.cover ? track.cover : defaultCover.id,
      version: {
        name: track.version,
        audio: track.fileId,
      },
      participants: formatParticipants.participants,
      invitations: formatParticipants.invitations,
    };
    const session = localStorage.getItem('chestmusic_session_id');
    const result = await createProject({ body, params: { session } });

    if ('error' in result) {
      console.log('Error');
    } else {
      const { data: project = {} } = await fetchProject(result.data.id);
      navigate(`/share/${project.id}/${project.versions[0].id}`);
      // navigate('/my-chest');
    }
  };

  const nextStep = () => {
    addOrUpdateParticipant({
      id: data.user_id,
      user: data.full_name,
      role: track.userRole,
      isOwner: true,
    });
  };

  const updatePreview = (state) => {
    if (state) setPreview(state);
  };

  const saveCover = () => {
    if (preview.local) {
      handleUploadImage();
    } else {
      setCover(preview.url);
      setTrack({
        ...track,
        cover: preview.id,
      });
      setOpen(false);
    }
  };

  const addOrUpdateParticipant = (user) => {
    const itemIndex = participants.findIndex((participant) => participant.id === user?.id);
    let list;

    if (itemIndex >= 0) {
      list = [...participants];
      list[itemIndex] = user;
    } else {
      list = [...participants, user];
    }

    setParticipants(list);
  };

  const removeParticipant = (user) => {
    setParticipants((current) => current.filter((participant) => participant.user !== user));
  };

  const updateParticipant = (user, role) => {
    const itemIndex = participants.findIndex((participant) => participant.user === user);
    const list = [...participants];

    list[itemIndex] = {
      ...list[itemIndex],
      role: role,
    };

    setParticipants(list);
  };

  const addParticipant = (user, role, id, isEmail) => {
    const list = [...participants, { user: user, role: role, id: id, isEmail: isEmail }];
    setParticipants(list);
  };

  const Participant = ({ user, role, isOwner }) => (
    <>
      <div className='flex flex-row items-center gap-3'>
        <div className='p-2 bg-black rounded-full'>
          {(() => {
            switch (role) {
              case 'artist':
                return <MicrophoneIcon className='h-5 w-5 text-brand-gold' />;
              case 'listener':
                return <MusicalNoteIcon className='h-5 w-5 text-white' />;
              case 'feat':
                return <MegaphoneIcon className='h-5 w-5 text-green-500' />;
              case 'producer':
                return <ComputerDesktopIcon className='h-5 w-5 text-violet-500' />;
              default:
                return <MicrophoneIcon className='h-5 w-5 text-brand-gold' />;
            }
          })()}
        </div>
        <div className='flex flex-row w-full items-center justify-center'>
          <div className='grow flex items-center'>
            <span className='!mb-0 text-ellipsis !text-white'>{user}</span>
          </div>
          <Dropdown
            list={isOwner ? roles.filter((role) => role !== 'listener') : roles}
            isOwner={isOwner}
            remove={() => removeParticipant(user)}
            selected={role}
            set={(role) => {
              updateParticipant(user, role);
            }}
          />
        </div>
      </div>
    </>
  );

  const handleSubmitForm = (e) => {
    e.preventDefault();
    nextStep();
    handleCreateProject();
  };

  const stepOne = () => {
    return (
      <div className='flex flex-col md:flex-row w-full '>
        <div className='bg-neutral-silver-700 rounded-t-2xl md:rounded-tl-2xl md:rounded-bl-2xl md:rounded-br-none md:rounded-tr-none px-4 md:px-8 py-8 md:py-[37px]  w-full order-2 md:order-1'>
          <h3 className='mb-4 md:mb-8 text-center text-[44px] md:text-[58px]'>{t('upload.title')}</h3>

          <form id='first-step' name='first-step' className='flex flex-col gap-5 md:px-4'>
            <Input
              type='text'
              name='name'
              required
              register={register}
              placeholder={t('global.write_here')}
              label={t('upload.track_name')}
              error={errors.name && t('global.required')}
            />
            <Select
              options={filteredRoles}
              name='role'
              label={t('upload.your_role')}
              value={track.userRole}
              onChange={handleOnChangeRole}
            />

            <div className='flex flex-col gap-2'>
              <label>{t('upload.participant')}</label>
              <AutoComplete
                options={roles}
                handleAdd={addParticipant}
                filter_ids={participants.map((participant) => participant.id)}
                className='!mb-0'
              />
            </div>

            <div className='flex flex-col gap-4'>
              {participants.map((user, index) => (
                <div key={index}>
                  <Participant user={user?.user} role={user?.role} isOwner={user.isOwner} />
                </div>
              ))}
            </div>
            <div className='flex flex-col gap-2 hidden md:flex '>
              <ProgressLine
                percentage={(progress.loaded * 100) / progress.total}
                colour={progress.loaded > 0 && progress.loaded === progress.total ? '#FFB447' : '#7C59DE'}
                error={progress.error}
                className='mb-4 '
              />
              <AnimatePresence>
                {progress.loaded > 0 && progress.loaded === progress.total ? (
                  <motion.div
                    className='flex items-center justify-center gap-1.5 text-brand-gold'
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    {t('upload.uploaded')} <CheckIcon className='h-4 w-4 text-brand-gold' />
                  </motion.div>
                ) : (
                  <motion.span className='font-archivo text-center text-brand-red' exit={{ opacity: 0 }}>
                    {!progress.error
                      ? `${bytesToSize(progress.loaded)} ${t('global.of')} ${bytesToSize(progress.total, 1)}`
                      : progress.error + t('upload.error_generic_ending')}
                  </motion.span>
                )}
              </AnimatePresence>
              <div
                className='flex items-center gap-1 py-3 grow rounded-xl justify-center text-sm'
                style={{ color: '#B296FF' }}
                data-tooltip-id='a'
              >
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='svg-icon'
                >
                  <path
                    d='M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </svg>
                {t('upload.bits_info')}
                <ReactTooltip
                  id='a'
                  style={{
                    width: '306px',
                    height: '78px',
                    padding: '12px',
                    gap: '10px',
                    borderRadius: '12px 12px 12px 12px',
                    background: '#E6E9ED',
                    color: '#000',
                  }}
                >
                  {t('upload.tooltip')}
                </ReactTooltip>
              </div>
            </div>
          </form>
        </div>

        <div className='w-full flex flex-col gap-4 md:gap-6 order-3 '>
          <div className='flex flex-col items-center justify-center bg-neutral-silver-700 rounded-b-2xl md:rounded-tr-2xl md:rounded-br-2xl md:rounded-bl-none md:rounded-tl-none px-8 pb-8 md:p-8 grow'>
            <div className='w-[180px] md:w-[250px] h-[180px] md:h-[250px]'>
              <TrackCoverPreview
                cover={cover}
                defaultCover={defaultCover}
                onClick={() => {
                  setOpen(true);
                }}
              />
            </div>
            <h4
              className={`mt-8 !text-center ${
                track.name ? 'text-white' : 'text-neutral-silver-200'
              } text-[40px] md:text-[48px]`}
            >
              {track.name ? track.name : 'track name'}
            </h4>
            <p className='text-neutral-silver-200 uppercase mt-2 !mb-0 text-base md:text-lg'></p>
            <div className='flex flex-col gap-2 md:hidden w-full mt-8'>
              <ProgressLine
                percentage={(progress.loaded * 100) / progress.total}
                colour={progress.loaded > 0 && progress.loaded === progress.total ? '#FFB447' : '#7C59DE'}
                error={progress.error}
                className='mb-4 '
              />
              <AnimatePresence>
                {progress.loaded > 0 && progress.loaded === progress.total ? (
                  <motion.div
                    className='flex items-center justify-center gap-1.5 text-brand-gold'
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    {t('upload.uploaded')} <CheckIcon className='h-4 w-4 text-brand-gold' />
                  </motion.div>
                ) : (
                  <motion.span className='font-archivo text-center text-brand-red' exit={{ opacity: 0 }}>
                    {!progress.error
                      ? `${bytesToSize(progress.loaded)} ${t('global.of')} ${bytesToSize(progress.total, 1)}`
                      : progress.error + t('upload.error_generic_ending')}
                  </motion.span>
                )}
              </AnimatePresence>
              <div
                className='flex items-center gap-1 py-3 grow rounded-xl justify-center text-sm'
                style={{ color: '#B296FF' }}
                data-tooltip-id='a'
              >
                <svg
                  width='18'
                  height='18'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='svg-icon'
                >
                  <path
                    d='M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
                    stroke='currentColor'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                  />
                </svg>
                {t('upload.bits_info')}
                <ReactTooltip
                  id='a'
                  style={{
                    width: '306px',
                    height: '78px',
                    padding: '12px',
                    gap: '10px',
                    borderRadius: '12px 12px 12px 12px',
                    background: '#E6E9ED',
                    color: '#000',
                  }}
                >
                  {t('upload.tooltip')}
                </ReactTooltip>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4 md:gap-6 w-full md:w-[80%] mt-8'>
              <Link to={'/my-chest'}>
                <Button text={t('global.cancel')} style='tertiary' />
              </Link>
              <Button
                text={t('global.continue')}
                style='primary'
                disabled={progress.loaded !== progress.total || isLoadingProject}
                form='first-step'
                onClick={handleSubmitForm}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return 'Loading...';
  }

  return (
    <>
      <div className='container py-8 lg:py-[60px]'>
        <div className='uploader p-4 md:p-[60px]'>{stepOne()}</div>
      </div>

      <Modal show={open} setShow={setOpen}>
        <div className='flex flex-col items-center text-center mb-[41px] max-w-[440px]'>
          <h4 className='mb-3 !text-5xl'>{t('upload.edit_cover')}</h4>
          <p className='text-neutral-silver-200 text-lg mb-6'>{t('upload.edit_instruction')}</p>
          <TrackCoverSelector
            selectedCover={preview}
            preview={preview?.url}
            updatePreview={updatePreview}
            covers={covers.concat(covers).sort()}
          />
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <Button
            text={t('global.cancel')}
            style='tertiary'
            onClick={() => {
              setOpen(false);
            }}
          />
          <Button
            text={t('global.save')}
            style='primary'
            onClick={saveCover}
            disabled={loadingImage}
            loading={loadingImage}
          />
        </div>
      </Modal>
    </>
  );
}
